import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SharedService } from 'app/modules/common/services/shared-service';
import { DespachoService } from '../despacho-colaboradores/services/despacho.service';
import { FileExportService } from 'app/modules/selection-generic/services/file-export.service';
import {
    VisitasEmpresaReportDialogResult,
    VisitasEmpresaReportPopupComponent
} from './popups/visitas-empresa-report-popup/visitas-empresa-report-popup.component';

interface VisitaResumen {
    id: number;
    fecha: string;
    fechaCompleta: string;
    realizada: boolean;
    mes: number;
}

interface Empresa {
    id: number;
    empresaId: number;
    nombre: string;
    visitas: { [key: string]: VisitaResumen[] };
    visitasOrdenadas: VisitaResumen[];
}

interface VisitaEmpresaDetalle {
    id: number;
    empresaId: number | null;
    coordinador1: string;
    coordinador2: string;
    tipo: string;
    asunto: string;
    asistentes: string;
    asistentesEmpresa: string;
    asistentesMutua: string;
    comentarios: string;
    resumen: string;
    realizada: string;
    frealizacion: string;
}

export interface Coordinador {
    id: number;
    nombre: string;
}

@Component({
    selector: 'app-visitas-empresa',
    templateUrl: './visitas-empresa.component.html',
    styleUrls: ['./visitas-empresa.component.scss'],
    standalone: false,
})
export class VisitasEmpresaComponent implements OnInit {
    @Output() onError = new EventEmitter<any>();
    @Output() onAction = new EventEmitter<{ action: string, data: any }>();

    year: number = new Date().getFullYear();
    coordinadorSeleccionado: number | null = null;
    coordinadorNombre: string = '';
    months: string[] = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

    empresas: Empresa[] = [];
    coordinadores: Coordinador[] = [];

    loading: boolean = false;
    reportLoading: boolean = false;
    error: string | null = null;
    empresaSeleccionado: number | null = null;
    visitaSeleccionadaId: number | null = null;

    fechaVisitaDetalle: string = '';
    coordinadorDetalle: string = '';
    tipoDetalle: string = '';
    asuntoDetalle: string = '';
    asistentesDetalle: string = '';
    asistentesEmpresaDetalle: string = '';
    asistentesMutuaDetalle: string = '';
    comentariosDetalle: string = '';
    resumenDetalle: string = '';

    constructor(
        private dialog: MatDialog,
        private despachoService: DespachoService,
        private sharedService: SharedService,
        private fileExportService: FileExportService,
    ) { }

    ngOnInit(): void {
        this.cargarCoordinadores();
    }

    cargarCoordinadores(): void {
        this.loading = true;
        this.error = null;

        try {
            const request$ = this.despachoService.GetAllCoordinatorsList();

            request$.subscribe(res => {
                if (res && Array.isArray(res.result)) {
                    this.coordinadores = this.normalizarCoordinadores(res.result);

                    if (this.coordinadores.length > 0) {
                        this.seleccionarCoordinador(this.coordinadores[0].id);
                        return;
                    }

                    this.empresas = [];
                } else {
                    this.error = 'No se pudieron cargar los coordinadores';
                }

                this.loading = false;
            }, err => {
                console.error(err);
                this.error = 'Error al cargar coordinadores';
                this.loading = false;
                this.onError.emit(err);
            });
        } catch (err) {
            console.error(err);
            this.error = 'Error al inicializar visitas de empresa';
            this.loading = false;
            this.onError.emit(err);
        }
    }

    cambiarCoordinador(event: Event): void {
        const value = (event.target as HTMLSelectElement).value;
        this.seleccionarCoordinador(value);
    }

    cambiarYear(incremento: number): void {
        this.year += incremento;

        if (this.coordinadorSeleccionado) {
            this.GetEmpresaList();
        }
    }

    seleccionarEmpresa(empresa: Empresa): void {
        this.empresaSeleccionado = empresa.empresaId;

        if (empresa.visitasOrdenadas.length === 0) {
            this.clearSelectedVisit();
            return;
        }

        const currentVisitBelongsToCompany = empresa.visitasOrdenadas.some(visit => visit.id === this.visitaSeleccionadaId);
        if (!currentVisitBelongsToCompany) {
            this.seleccionarVisita(empresa, empresa.visitasOrdenadas[0]);
        }
    }

    seleccionarVisita(empresa: Empresa, visita: VisitaResumen, event?: MouseEvent): void {
        event?.stopPropagation();

        this.empresaSeleccionado = empresa.empresaId;
        this.visitaSeleccionadaId = visita.id;
        this.fechaVisitaDetalle = visita.fechaCompleta;
        this.coordinadorDetalle = this.coordinadorNombre;
        this.tipoDetalle = '';
        this.asuntoDetalle = '';
        this.asistentesDetalle = '';
        this.asistentesEmpresaDetalle = '';
        this.asistentesMutuaDetalle = '';
        this.comentariosDetalle = '';
        this.resumenDetalle = '';

        try {
            const request$ = this.despachoService.GetVisitaEmpresaDetails(visita.id.toString());

            request$.subscribe(res => {
                if (res && res.code === 0 && res.result) {
                    if (this.visitaSeleccionadaId !== visita.id) {
                        return;
                    }

                    this.aplicarDetalleVisita(res.result);
                } else {
                    this.clearSelectedVisit();
                    this.error = 'No se pudieron cargar los detalles de la visita';
                }
            }, err => {
                console.error(err);
                this.clearSelectedVisit();
                this.error = 'Error al cargar los detalles de la visita';
                this.onError.emit(err);
            });
        } catch (err) {
            console.error(err);
            this.clearSelectedVisit();
            this.error = 'Error al inicializar el detalle de la visita';
            this.onError.emit(err);
        }
    }

    public GetEmpresaList(): void {
        if (!this.coordinadorNombre) {
            this.empresas = [];
            this.loading = false;
            this.sharedService.ShowSnackBar('Por favor seleccione un coordinador');
            return;
        }

        this.loading = true;
        this.error = null;
        this.empresaSeleccionado = null;
        this.clearSelectedVisit();

        try {
            const request$ = this.despachoService.getEmpresasVisitaList(
                this.coordinadorNombre,
                this.year.toString()
            );

            request$.subscribe(res => {
                if (res && res.code === 0) {
                    this.empresas = Array.isArray(res.result) ? this.procesarDatosEmpresas(res.result) : [];
                } else {
                    this.empresas = [];
                    this.sharedService.ShowSnackBar('No se pudo cargar las empresas del coordinador.');
                }

                this.loading = false;
            }, err => {
                console.error(err);
                this.empresas = [];
                this.loading = false;
                this.sharedService.ShowSnackBar('Se produjo un error, actualice la pagina.');
                this.onError.emit(err);
            });
        } catch (err) {
            console.error(err);
            this.empresas = [];
            this.loading = false;
            this.error = 'Error al inicializar la carga de empresas';
            this.onError.emit(err);
        }
    }

    crear(): void {
        this.onAction.emit({
            action: 'crear',
            data: {
                coordinadorId: this.coordinadorSeleccionado,
                coordinador: this.coordinadorNombre,
                empresaId: this.empresaSeleccionado,
                year: this.year
            }
        });
    }

    eliminar(): void {
        if (!this.visitaSeleccionadaId) {
            this.error = 'Debe seleccionar una visita para eliminar';
            return;
        }

        this.onAction.emit({
            action: 'eliminar',
            data: { id: this.visitaSeleccionadaId }
        });
    }

    editar(): void {
        if (!this.visitaSeleccionadaId) {
            this.error = 'Debe seleccionar una visita para editar';
            return;
        }

        this.onAction.emit({
            action: 'editar',
            data: { id: this.visitaSeleccionadaId }
        });
    }

    guardar(): void {
        this.onAction.emit({ action: 'guardar', data: this.empresas });
    }

    deshacer(): void {
        this.GetEmpresaList();
    }

    generarInformeWord(): void {
        if (!this.visitaSeleccionadaId) {
            this.sharedService.ShowSnackBar('Debe seleccionar una visita para generar el Word');
            return;
        }

        this.reportLoading = true;
        this.error = null;

        this.despachoService.GetVisitaEmpresaWordReport(this.visitaSeleccionadaId.toString()).subscribe(res => {
            this.reportLoading = false;

            if (res && res.code === 0 && res.result && res.result.file && res.result.fileName) {
                this.fileExportService.DownloadFile(res.result.file, res.result.fileName);
                return;
            }

            this.sharedService.ShowSnackBar('No se pudo generar el informe Word');
        }, err => {
            console.error(err);
            this.reportLoading = false;
            this.sharedService.ShowSnackBar('No se pudo generar el informe Word');
            this.onError.emit(err);
        });
    }

    generarInformeExcel(): void {
        if (!this.coordinadorNombre) {
            this.sharedService.ShowSnackBar('Debe seleccionar un coordinador para generar el Excel');
            return;
        }

        this.reportLoading = true;
        this.error = null;

        this.despachoService.GetVisitaEmpresaExcelReport(this.coordinadorNombre, this.year.toString()).subscribe(res => {
            this.reportLoading = false;

            if (res && res.code === 0 && res.result && res.result.file && res.result.fileName) {
                this.fileExportService.DownloadFile(res.result.file, res.result.fileName);
                return;
            }

            this.sharedService.ShowSnackBar('No se pudo generar el Excel');
        }, err => {
            console.error(err);
            this.reportLoading = false;
            this.sharedService.ShowSnackBar('No se pudo generar el Excel');
            this.onError.emit(err);
        });
    }

    abrirInformeRango(): void {
        if (!this.coordinadorNombre) {
            this.sharedService.ShowSnackBar('Debe seleccionar un coordinador para generar el informe');
            return;
        }

        const selectedEmpresa = this.getEmpresaSeleccionada();
        const dialogRef = this.dialog.open(VisitasEmpresaReportPopupComponent, {
            width: '460px',
            autoFocus: false,
            data: {
                coordinador: this.coordinadorNombre,
                year: this.year,
                empresaId: this.empresaSeleccionado,
                empresaNombre: selectedEmpresa ? selectedEmpresa.nombre : ''
            }
        });

        dialogRef.afterClosed().subscribe((result: VisitasEmpresaReportDialogResult | undefined) => {
            if (!result) {
                return;
            }

            this.generarInformeWordRango(result);
        });
    }

    generarInformeWordRango(result: VisitasEmpresaReportDialogResult): void {
        if (result.scope === 'empresa' && !this.empresaSeleccionado) {
            this.sharedService.ShowSnackBar('Debe seleccionar una empresa para generar el informe');
            return;
        }

        this.reportLoading = true;
        this.error = null;

        this.despachoService.GetVisitaEmpresaWordReportByRange({
            coordinador: this.coordinadorNombre,
            empresaId: result.scope === 'empresa' ? this.empresaSeleccionado : null,
            dateFrom: result.dateFrom,
            dateTo: result.dateTo,
            scope: result.scope
        }).subscribe(res => {
            this.reportLoading = false;

            if (res && res.code === 0 && res.result && res.result.file && res.result.fileName) {
                this.fileExportService.DownloadFile(res.result.file, res.result.fileName);
                return;
            }

            this.sharedService.ShowSnackBar('No hay datos para generar el informe');
        }, err => {
            console.error(err);
            this.reportLoading = false;
            this.sharedService.ShowSnackBar('No se pudo generar el informe');
            this.onError.emit(err);
        });
    }

    getEmpresaSeleccionada(): Empresa | undefined {
        if (!this.empresaSeleccionado) {
            return undefined;
        }

        return this.empresas.find(empresa => empresa.empresaId === this.empresaSeleccionado);
    }

    private seleccionarCoordinador(id: number | string): void {
        const coord = this.coordinadores.find(c => c.id == id);

        if (!coord) {
            this.coordinadorSeleccionado = null;
            this.coordinadorNombre = '';
            this.empresas = [];
            this.clearSelectedVisit();
            return;
        }

        this.coordinadorSeleccionado = coord.id;
        this.coordinadorNombre = coord.nombre;
        this.GetEmpresaList();
    }

    private normalizarCoordinadores(coordinadores: any[]): Coordinador[] {
        return coordinadores
            .map(c => ({
                id: c.id ?? c.Id ?? c.coordinadorId ?? c.CoordinadorId,
                nombre: c.nombre ?? c.Nombre ?? c.name ?? c.Name
            }))
            .filter(c => c.id !== undefined && c.id !== null && !!c.nombre);
    }

    private procesarDatosEmpresas(datos: any[]): Empresa[] {
        const empresasPorId: { [key: number]: Empresa } = {};

        datos.forEach(item => {
            const empresaId = Number(item.empresaId ?? item.EmpresaId);
            if (!empresaId) {
                return;
            }

            if (!empresasPorId[empresaId]) {
                empresasPorId[empresaId] = {
                    id: Number(item.id ?? item.Id),
                    empresaId,
                    nombre: item.empresa ?? item.Empresa ?? 'Sin nombre',
                    visitas: {},
                    visitasOrdenadas: []
                };
            }

            const mes = Number(item.mes ?? item.Mes);
            if (!mes || mes < 1 || mes > this.months.length) {
                return;
            }

            const visitaId = Number(item.id ?? item.Id);
            const realizada = (item.estaRealizada ?? item.EstaRealizada) === 1;
            const visita: VisitaResumen = {
                id: visitaId,
                fecha: this.buildFechaResumen(item.fechaVisita ?? item.FechaVisita, realizada),
                fechaCompleta: this.buildFechaCompleta(item.fechaVisita ?? item.FechaVisita),
                realizada,
                mes
            };

            const monthKey = this.months[mes - 1];
            if (!empresasPorId[empresaId].visitas[monthKey]) {
                empresasPorId[empresaId].visitas[monthKey] = [];
            }

            empresasPorId[empresaId].visitas[monthKey].push(visita);
            empresasPorId[empresaId].visitasOrdenadas.push(visita);
        });

        const empresas = Object.values(empresasPorId);
        empresas.forEach(empresa => {
            empresa.visitasOrdenadas.sort((a, b) => this.getVisitaSortValue(b) - this.getVisitaSortValue(a));

            this.months.forEach(month => {
                if (empresa.visitas[month]) {
                    empresa.visitas[month].sort((a, b) => this.getVisitaSortValue(a) - this.getVisitaSortValue(b));
                }
            });
        });

        return empresas;
    }

    private aplicarDetalleVisita(detalle: VisitaEmpresaDetalle): void {
        this.fechaVisitaDetalle = this.formatDate(detalle.realizada || detalle.frealizacion);
        this.coordinadorDetalle = detalle.coordinador1 ?? '';
        this.tipoDetalle = detalle.tipo ?? '';
        this.asuntoDetalle = detalle.asunto ?? '';
        this.asistentesDetalle = detalle.asistentes ?? '';
        this.asistentesEmpresaDetalle = detalle.asistentesEmpresa ?? '';
        this.asistentesMutuaDetalle = detalle.asistentesMutua ?? '';
        this.comentariosDetalle = detalle.comentarios ?? '';
        this.resumenDetalle = detalle.resumen ?? '';
        this.error = null;
    }

    private clearSelectedVisit(): void {
        this.visitaSeleccionadaId = null;
        this.fechaVisitaDetalle = '';
        this.coordinadorDetalle = '';
        this.tipoDetalle = '';
        this.asuntoDetalle = '';
        this.asistentesDetalle = '';
        this.asistentesEmpresaDetalle = '';
        this.asistentesMutuaDetalle = '';
        this.comentariosDetalle = '';
        this.resumenDetalle = '';
    }

    private buildFechaResumen(value: string, realizada: boolean): string {
        const fecha = value ? String(value) : '';
        if (fecha) {
            return fecha;
        }

        return realizada ? '' : '**';
    }

    private buildFechaCompleta(value: string): string {
        const fecha = value ? String(value) : '';
        if (!fecha) {
            return '';
        }

        if (fecha.split('/').length === 2) {
            return `${fecha}/${this.year}`;
        }

        return fecha;
    }

    private getVisitaSortValue(visita: VisitaResumen): number {
        const parts = visita.fechaCompleta.split('/');
        if (parts.length !== 3) {
            return visita.mes * 100;
        }

        const day = Number(parts[0]);
        const month = Number(parts[1]);
        const year = Number(parts[2]);

        if ([day, month, year].some(value => Number.isNaN(value))) {
            return visita.mes * 100;
        }

        return new Date(year, month - 1, day).getTime();
    }

    private formatDate(value: string): string {
        if (!value) {
            return '';
        }

        const normalizedValue = String(value);
        const isoMatch = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoMatch) {
            return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
        }

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return value;
        }

        const day = `${date.getDate()}`.padStart(2, '0');
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const year = date.getFullYear();

        return `${day}/${month}/${year}`;
    }
}
